'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Building2, Home, HomeIcon, ArrowRight } from 'lucide-react';

interface PropertyCounts {
  [key: string]: number;
}

export default function BrowseResidentialTypes() {
  const [counts, setCounts] = useState<PropertyCounts>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch all properties and count residential types
    fetch('/api/properties?limit=1000&listing_type=rent')
      .then(res => res.json())
      .then(response => {
        const props = response.data?.properties || response.properties || [];

        const typeCounts: PropertyCounts = {
          condo: 0,
          house: 0,
          house_and_lot: 0,
          townhouse: 0
        };

        props.forEach((prop: any) => {
          const type = prop.property_type;
          if (typeCounts.hasOwnProperty(type)) {
            typeCounts[type]++;
          }
        });

        const combinedCounts = {
          condo: typeCounts.condo,
          house: typeCounts.house + typeCounts.house_and_lot,
          townhouse: typeCounts.townhouse
        };

        setCounts(combinedCounts);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading residential counts:', err);
        setLoading(false);
      });
  }, []);

  const propertyTypes = [
    {
      icon: Building2,
      title: 'Condos for Rent',
      description: 'Modern condominiums in prime locations with amenities',
      features: ['Security', 'Amenities', 'City Living'],
      type: 'condo',
      href: '/properties?property_type=condo&listing_type=rent',
      gradient: 'from-cyan-500 to-blue-500'
    },
    {
      icon: Home,
      title: 'Houses for Rent',
      description: 'Spacious houses perfect for families with yards and parking',
      features: ['Privacy', 'Space', 'Family-Friendly'],
      type: 'house',
      href: '/properties?property_type=house&listing_type=rent',
      gradient: 'from-blue-500 to-indigo-500'
    },
    {
      icon: HomeIcon,
      title: 'Townhouses for Rent',
      description: 'Modern townhouses with privacy and community living',
      features: ['Private', 'Community', 'Flexible Space'],
      type: 'townhouse',
      href: '/properties?property_type=townhouse&listing_type=rent',
      gradient: 'from-indigo-500 to-purple-500'
    }
  ];

  return (
    <section className="bg-gradient-to-b from-cyan-50/30 to-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-100 rounded-full mb-4">
            <Home className="w-4 h-4 text-cyan-600" />
            <span className="text-sm font-semibold text-cyan-600">Residential Properties</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Find Your Perfect Home
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Explore condos, houses, and townhouses designed for your lifestyle and comfort
          </p>
        </div>

        {/* Property Types Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {propertyTypes.map((type, index) => {
            const Icon = type.icon;
            return (
              <Link
                key={index}
                href={type.href}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-cyan-400 hover:shadow-xl transition-all duration-300"
              >
                {/* Gradient Header */}
                <div className={`h-2 bg-gradient-to-r ${type.gradient}`}></div>

                {/* Content */}
                <div className="p-6">
                  {/* Icon */}
                  <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-cyan-50 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-7 h-7 text-cyan-600" />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-cyan-600 transition-colors">
                    {type.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    {type.description}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {type.features.map((feature, idx) => (
                      <span
                        key={idx}
                        className="text-xs px-3 py-1 bg-gray-100 text-gray-700 rounded-full"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>

                  {/* CTA */}
                  <div className="flex items-center gap-2 text-cyan-600 font-semibold group-hover:gap-3 transition-all">
                    {loading ? (
                      <span className="text-sm">Loading...</span>
                    ) : (
                      <span className="text-sm">{counts[type.type] || 0} Available</span>
                    )}
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* View All Button */}
        <div className="text-center mt-12">
          <Link
            href="/properties?listing_type=rent"
            className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 hover:bg-cyan-600 text-white rounded-xl font-bold transition-all shadow-lg hover:shadow-xl group"
          >
            View All Residential Properties
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
          <p className="text-sm text-gray-500 mt-4">
            Verified homes available across Metro Manila
          </p>
        </div>
      </div>
    </section>
  );
}
